<template>
  <span v-if="visible">
    <i-btn
      :classes="classes"
      :loading="loading"
      :title="$t('open_whatsapp_chat')"
      color="success"
      icon="fab fa-whatsapp"
      small
      text
      @click="open()"
    />
    <!-- Se monta recién al hacer falta: en el listado de ventas este botón
         aparece una vez por fila, y un diálogo por fila es mucho para algo
         que casi nunca se abre. -->
    <start-chat-dialog
      v-if="dialog"
      :customer="customer"
      @close="dialog = false"
      @started="opened"
    />
  </span>
</template>
<script>
import { mapGetters } from 'vuex'
import StartChatDialog from './StartChatDialog.vue'
export default {
  name: 'customer-chat-button',
  components: {
    StartChatDialog
  },
  props: {
    /**
     * Identificador del cliente al que se le quiere escribir.
     */
    customer: {
      type: [Number, String],
      default: null
    },
    /**
     * Orden desde la que se abre, cuando se abre desde una. Sirve para llegar
     * a la conversación de la que salió ese trabajo y no a la última del
     * cliente, que puede ser de otra cosa.
     */
    order: {
      type: [Number, String],
      default: null
    },
    classes: {
      type: [String, Object],
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      dialog: false
    }
  },
  computed: {
    ...mapGetters({
      me: 'session/me',
      company: 'company/getCompanyData',
      getPermissions: 'session/getPermissions',
      isAdmin: 'session/isAdmin',
      hasAddon: 'whatsapp/getHasAddon'
    }),
    /**
     * Whatsapp se vende como complemento, así que el botón no existe para la
     * imprenta que no lo contrató ni para el usuario sin permiso de bandeja.
     * Son los mismos permisos que abren la pantalla a la que lleva: mostrarlo
     * igual sería ofrecer un camino que termina en un cartel de sin acceso.
     */
    visible() {
      if (!this.me || !this.company || !this.customer || !this.hasAddon) {
        return false
      }
      return (
        this.isAdmin ||
        this.getPermissions([
          'whatsapp.view_inbox',
          'whatsapp.view_conversation'
        ])
      )
    },
    /**
     * Escribirle primero a un cliente manda una plantilla y eso se cobra, así
     * que se ofrece sólo a quien puede responder conversaciones. Al resto se
     * le avisa que el cliente no escribió y nada más.
     */
    canWrite() {
      return (
        this.isAdmin || this.getPermissions(['whatsapp.reply_conversation'])
      )
    }
  },
  methods: {
    /**
     * Pide una conversación al servidor con los filtros dados.
     *
     * Se pide un solo registro porque el listado ya devuelve primero la más
     * reciente, que es la que hay que abrir.
     *
     * @param {Object} filters - Filtros del listado de conversaciones.
     * @returns {Object} La conversación, o null si no hay ninguna.
     */
    async find(filters) {
      const response = await this.$api.whatsapp.list({
        opt: {
          params: { company_id: this.company.pk, size: 1, ...filters },
          skipErrorToast: true
        }
      })

      const datos = response.data
      const resultados = datos.results !== undefined ? datos.results : datos

      return resultados.length > 0 ? resultados[0] : null
    },
    /**
     * Busca la conversación y abre la bandeja en ese hilo.
     *
     * Primero la de la orden, cuando se abre desde una: lleva al hilo donde
     * se cotizó ese trabajo y no a la última charla del cliente, que puede
     * ser de otra cosa. Si esa orden no salió de Whatsapp, cae al cliente.
     *
     * La búsqueda se hace acá y no en la bandeja a propósito: si el cliente
     * todavía no escribió hay que poder ofrecer escribirle sin sacar al
     * usuario de la pantalla en la que está trabajando.
     */
    async open() {
      this.loading = true

      try {
        let conversation = this.order
          ? await this.find({ linked: `order:${this.order}` })
          : null

        if (conversation === null) {
          conversation = await this.find({ customer: this.customer })
        }

        if (conversation === null) {
          if (this.canWrite) {
            this.dialog = true
          } else {
            this.$toast.info(this.$t('customer_without_conversation'))
          }
          return
        }

        this.goTo(conversation)
      } catch (e) {
        // Si la imprenta dejó de tener el complemento mientras tenía la
        // pantalla abierta, el interceptor ya la lleva a la pantalla que lo
        // ofrece: un aviso rojo encima sería el segundo.
        const datos = e.response ? e.response.data : null

        if (!datos || datos.code !== 'whatsapp_addon_disabled') {
          this.$toast.error(this.$t('conversation_not_opened'))
        }
      } finally {
        this.loading = false
      }
    },
    /**
     * Abre la bandeja en una conversación.
     * @param {Object} conversation - Conversación a mostrar.
     */
    goTo(conversation) {
      this.$router.push({
        name: 'whatsapp',
        query: { conversation: String(conversation.pk) }
      })
    },
    /**
     * La conversación recién abierta: se cierra el diálogo y se entra al hilo
     * para que quien escribió vea lo que acaba de mandar.
     * @param {Object} conversation - Conversación devuelta por el servidor.
     */
    opened(conversation) {
      this.dialog = false
      this.goTo(conversation)
    }
  }
}
</script>
